import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeSlash, LockKey } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const inputCls = "w-full rounded-xl border border-[#EBE4DA] bg-white px-4 py-3 pr-12 text-base text-[#2D2824] focus:outline-none focus:ring-2 focus:ring-[#36858B]/40";

const PwField = ({ id, label, value, onChange, show, autoComplete }) => (
  <div>
    <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-[#2D2824]">{label}</label>
    <input id={id} data-testid={id} type={show ? "text" : "password"} autoComplete={autoComplete} maxLength={200}
      value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} />
  </div>
);

export default function ChangePassword() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (v) => setForm({ ...form, [k]: v });
  const mismatch = form.confirm && form.next !== form.confirm;
  const short = form.next && form.next.length < 10;
  const canSubmit = form.current && form.next.length >= 10 && form.next === form.confirm && !busy;

  if (user && user.role !== "admin") {
    return <main className="mx-auto max-w-lg px-5 py-12"><p data-testid="change-password-owner-only" className="text-[#4A4540]">This page is for the site owner only.</p></main>;
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await api.post("/auth/change-password", {
        current_password: form.current, new_password: form.next, confirm_password: form.confirm });
      localStorage.setItem("token", r.data.access_token);
      setForm({ current: "", next: "", confirm: "" });
      await refreshUser();
      toast.success(r.data.message);
      navigate("/admin/payments");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't change your password. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="change-password-page" className="mx-auto max-w-lg px-5 py-8 sm:py-12">
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#E06D53]"><LockKey size={16} weight="duotone" /> Owner only</p>
      <h1 className="mt-2 font-serif text-4xl text-[#2C2A28] sm:text-5xl">Change password</h1>
      <p className="mt-2 text-[#6E665E]">After changing it, every other device signed in to this account is signed out.</p>
      <form onSubmit={submit} className="mt-6 space-y-4 rounded-2xl bg-white p-5 ring-1 ring-[#EBE4DA]">
        <PwField id="current-password" label="Current password" value={form.current} onChange={set("current")} show={show} autoComplete="current-password" />
        <PwField id="new-password" label="New password" value={form.next} onChange={set("next")} show={show} autoComplete="new-password" />
        <p className={`-mt-2 text-xs ${short ? "text-[#A84A32]" : "text-[#8A7F76]"}`}>At least 10 characters. A short sentence you'll remember works well.</p>
        <PwField id="confirm-password" label="New password again" value={form.confirm} onChange={set("confirm")} show={show} autoComplete="new-password" />
        {mismatch && <p data-testid="password-mismatch" className="-mt-2 text-xs text-[#A84A32]">The two new passwords don't match.</p>}
        <button type="button" data-testid="toggle-password-visibility" onClick={() => setShow(!show)}
          className="flex min-h-[44px] items-center gap-2 text-sm font-medium text-[#36858B]">
          {show ? <EyeSlash size={18} /> : <Eye size={18} />} {show ? "Hide passwords" : "Show passwords"}
        </button>
        {error && <p data-testid="change-password-error" role="alert" className="rounded-xl bg-[#FDECE8] px-4 py-3 text-sm text-[#A84A32]">{error}</p>}
        <button type="submit" data-testid="change-password-submit" disabled={!canSubmit}
          className="w-full rounded-full bg-[#E06D53] py-4 text-base font-semibold text-white hover:bg-[#C95A42] disabled:cursor-not-allowed disabled:opacity-50">
          {busy ? "Changing…" : "Change password"}
        </button>
      </form>
    </main>
  );
}
